import Image from "next/image";
import Link from "next/link";
import { useAuth } from "@/contexts/auth/AuthContext";
import { useSavedResources } from "@/contexts/savedResources/SavedResourcesContext";
import Analytics from "@/lib/analytics";
import { Activity } from "@/lib/models";
import Stopwatch from "./icons/Stopwatch";
import { HtmlRenderer } from "./HtmlRenderer";
import BookmarkCheckedIcon from "./icons/BookmarkChecked";
import BookmarkIcon from "./icons/Bookmark";
import { ChangeEvent } from "react";
import { Checkbox } from "./Checkbox";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "@/components/StyledButton";

export enum ActivityCardCornerElement {
  None = "none",
  Checkbox = "checkbox",
  Bookmark = "bookmark",
}

type ActivityCardProps = {
  activity: Activity;
  index?: number;
  className?: string;
  onAddToSavedResources?: () => void;
  cornerElement?: ActivityCardCornerElement;
  onRemoveFromSavedResources?: () => void;
  onUpdateSavedResourcesError?: () => void;
  isSelected?: boolean;
  handleCheckboxChange?: (e: ChangeEvent<HTMLInputElement>) => void;
};

const ActivityCard = ({
  activity,
  index,
  className,
  onAddToSavedResources,
  onRemoveFromSavedResources,
  onUpdateSavedResourcesError,
  cornerElement = ActivityCardCornerElement.Bookmark,
  isSelected,
  handleCheckboxChange,
}: ActivityCardProps) => {
  const {
    activities: savedActivities,
    addActivity,
    removeActivity,
  } = useSavedResources();
  const { user, openLoginPrompt } = useAuth();

  const handleActivityClick = () => {
    Analytics.handleActivityCardClick({
      activity,
      carouselIndex: index,
    });
  };

  const isSaved = savedActivities.some(
    (savedActivity) => savedActivity.slug === activity.urlSlug,
  );

  const handleSavedResourcesClick = async () => {
    if (!user) {
      openLoginPrompt("activity");
      return;
    }

    try {
      if (isSaved) {
        removeActivity(activity.urlSlug);
        onRemoveFromSavedResources && onRemoveFromSavedResources();
      } else {
        addActivity(activity.urlSlug);
        onAddToSavedResources && onAddToSavedResources();
      }
    } catch (e) {
      console.error(e);
      onUpdateSavedResourcesError && onUpdateSavedResourcesError();
    }
  };

  return (
    <div
      className={`flex min-w-[300px] flex-col justify-between overflow-hidden rounded-xl border bg-white ${className}`}
    >
      <Link
        href={`/activities/${activity.urlSlug}`}
        onClick={handleActivityClick}
        shallow={true}
        aria-label={activity.name}
      >
        <div>
          {!!activity.activityCard?.sourceUrl ? (
            <Image
              src={activity.activityCard?.sourceUrl}
              alt={activity.name}
              width={448}
              height={252}
              className="flex w-auto grow"
            />
          ) : (
            <div
              className={`h-[252px] w-[448px] bg-${activity.learnerOutcomeId}-30`}
            />
          )}
        </div>
        <div className="mx-4 my-6 font-roobert text-xl font-semibold text-neutral-800">
          {activity.name}
        </div>
        <div className="mx-4 mb-4 line-clamp-3 text-base font-normal leading-tight text-neutral-600">
          <HtmlRenderer html={activity.overview} />
        </div>
      </Link>
      <div className="mx-4 mb-6 flex items-center justify-between">
        <div className="flex">
          <Stopwatch />
          <span className="ml-1 text-base">{activity.runTime}</span>
        </div>

        {cornerElement == ActivityCardCornerElement.Checkbox && (
          <Checkbox
            className="mr-3 text-base medium:mr-4"
            onChange={handleCheckboxChange}
            checked={isSelected}
          />
        )}

        {cornerElement == ActivityCardCornerElement.Bookmark && (
          <StyledButton
            buttonType={ButtonTypes.Tertiary}
            size={ButtonSizes.Small}
            iconType={IconTypes.Only}
            onClick={handleSavedResourcesClick}
            aria-label={
              isSaved ? "Remove from saved resources" : "Add to saved resources"
            }
          >
            {isSaved ? <BookmarkCheckedIcon /> : <BookmarkIcon />}
          </StyledButton>
        )}
      </div>
    </div>
  );
};

export default ActivityCard;
